import React, { useState, useEffect, useRef } from "react";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
} from "lucide-react";
import { useLanguageStore } from "../store/languageStore";
import { useChat } from "../hooks/useChat";

const ChatAdvisor: React.FC = () => {
  const [showChat, setShowChat] = useState(false);
  const [userText, setUserText] = useState("");
  const { selectedLanguage } = useLanguageStore();
  const { messages, isLoading, sendMessage } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const initialMessages = {
    EN: "Hi! I can help explain these programs and answer questions about eligibility. What would you like to know?",
    ES: "¡Hola! Puedo ayudarte a explicar estos programas y responder preguntas sobre elegibilidad. ¿Qué te gustaría saber?",
    ZH: "你好！我可以帮助解释这些项目并回答有关资格的问题。你想了解什么？",
    VI: "Xin chào! Tôi có thể giúp giải thích các chương trình này và trả lời câu hỏi về điều kiện. Bạn muốn biết gì?",
    FIL: "Hello! Makakatulong ako na ipaliwanag ang mga programang ito at sagutin ang mga tanong tungkol sa pagiging kwalipikado. Ano ang gusto mong malaman?",
    KO: "안녕하세요! 이러한 프로그램에 대해 설명하고 자격에 대한 질문에 답변해드릴 수 있습니다. 무엇을 알고 싶으신가요?",
    AR: "مرحباً! يمكنني المساعدة في شرح هذه البرامج والإجابة على الأسئلة حول الأهلية. ما الذي تريد معرفته؟",
  };

  const placeholders = {
    EN: "Type your message...",
    ES: "Escribe tu mensaje...",
    ZH: "输入您的消息...",
    VI: "Nhập tin nhắn của bạn...",
    FIL: "I-type ang iyong mensahe...",
    KO: "메시지를 입력하세요...",
    AR: "اكتب رسالتك...",
  };

  const titles = {
    EN: "Chat Advisor",
    ES: "Asesor de Chat",
    ZH: "聊天顾问",
    VI: "Cố Vấn Trò Chuyện",
    FIL: "Chat Advisor",
    KO: "채팅 상담사",
    AR: "مستشار المحادثة",
  };
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize with welcome message when chat opens
  useEffect(() => {
    if (showChat && messages.length === 0) {
      // Add initial message without calling the API
      // This is just a UI message, not stored in the database
    }
  }, [showChat, messages.length]);

  const handleSend = async () => {
    if (!userText.trim() || isLoading) return;

    const messageToSend = userText.trim();
    setUserText("");

    try {
      await sendMessage(messageToSend);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setShowChat(false);
    // Optionally clear chat when closing
    // clearChat();
  };

  // Combine initial message with actual messages for display
  const displayMessages = [
    {
      role: "assistant" as const,
      content: initialMessages[selectedLanguage],
      timestamp: Date.now() - 1000,
    },
    ...messages,
  ];

  return (
    <>
      <button
        onClick={() => setShowChat(true)}
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 z-50 focus:outline-none ring-2 ring-teal-500 dark:ring-teal-400"
        aria-label="Open chat advisor"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>

      {showChat && (
        <>
          <div
            className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-40"
            onClick={handleClose}
          />

          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-[calc(100%-2rem)] h-[70vh] max-h-[600px] flex flex-col animate-scale-in"
            >
              <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                <h3 className="text-lg font-semibold">{titles[selectedLanguage]}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleClose}
                    className="text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none ring-2 ring-teal-500 dark:ring-teal-400 rounded"
                    aria-label="Close chat advisor"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4 overflow-y-auto">
                <div className="space-y-4">
                  {displayMessages.map((message, index) => (
                    <div
                      key={`${message.timestamp}-${index}`}
                      className={`flex ${
                        message.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 shadow-sm animate-fade-in ${
                          message.role === "user"
                            ? "bg-[#DCF8C6] dark:bg-[#064E3B] text-light-heading dark:text-dark-heading"
                            : "bg-[#E5E7EB] dark:bg-[#374151] text-light-heading dark:text-dark-heading"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">
                          {message.content}
                        </p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-[#E5E7EB] dark:bg-[#374151] rounded-lg p-3 shadow-sm">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            Thinking...
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>
              <div className="p-4 border-t dark:border-gray-700">
                <div className="flex gap-2 relative">
                  <input
                    type="text"
                    value={userText}
                    onChange={(e) => setUserText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={placeholders[selectedLanguage]}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none ring-2 ring-teal-500 dark:ring-teal-400 placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleSend}
                    disabled={isLoading || !userText.trim()}
                    className="bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 text-white rounded-md p-2 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none ring-2 ring-teal-500 dark:ring-teal-400"
                    aria-label="Send message"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ChatAdvisor;
